import axios, { isAxiosError } from 'axios';

import { API_BASE_URL } from '~config';
import { authService } from '~libs';

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

const STATUS_MESSAGES: Record<number, string> = {
  400: 'Invalid request',
  401: 'Authentication required',
  403: 'Access denied',
  404: 'Resource not found',
  408: 'Request timed out',
  422: 'Validation error',
  429: 'Too many requests — try again later',
};

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

/**
 * Extracts a user-friendly message from an API error.
 * Prefers the interceptor's `userMessage`, falls back to response body, then Axios message.
 *
 * @param error The error object thrown by an API request
 * @return A string message suitable for displaying to end users, or undefined if no message is available
 *
 * @example
 * ```ts
 * try {
 *   await apiClient.get('/some-endpoint');
 * } catch (error) {
 *   const message = getApiErrorMessage(error);
 *   showNotification(message ?? 'An unexpected error occurred');
 * }
 * ```
 */
const getApiErrorMessage = (error: unknown | undefined): string | undefined => {
  if (!error) return undefined;

  if (isAxiosError(error)) {
    return error.response?.data?.userMessage ?? error.response?.data?.message ?? error.message ?? 'Unknown error';
  }

  if (error instanceof Error) return error.message;

  return 'Unknown error';
};

/* -------------------------------------------------------------------------- */
/*                                 Api Client                                 */
/* -------------------------------------------------------------------------- */

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(
  async (config) => {
    try {
      const token = await authService.getValidToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Failed to get token for API request:', error);
      authService.clearAuth();
      return Promise.reject(error);
    }
    return config;
  },
  (error) => Promise.reject(error),
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;

      if (status === 401)
        // Invalidate only the cached token on 401 — keeps the account so silent refresh can recover
        authService.invalidateToken();

      // 5xx: preserve the server's actual message; 4xx: use short mapped message
      const userMessage =
        status >= 500
          ? (data?.message ?? data?.detail ?? `Server error (${status})`)
          : (STATUS_MESSAGES[status] ?? `Request failed (${status})`);

      error.response.data = { ...data, userMessage };
    }
    return Promise.reject(error);
  },
);

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export { apiClient, getApiErrorMessage };
